import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAuthProvider, type Me } from './api.ts';
import { createAuth, loadConfig, type AppConfig } from './auth.ts';
import { DevSwitcher } from './components/DevSwitcher.tsx';
import { Header } from './components/Header.tsx';
import { Icon } from './components/Icon.tsx';
import { ErrorBox, Loading } from './components/ui.tsx';
import { navigate, useRoute, type Route } from './router.ts';
import { makeSession, SessionContext, useSession } from './session.tsx';
import { StreamProvider } from './stream.tsx';
import { softphoneFor } from './telephony/index.ts';
import { CallPanel } from './telephony/CallPanel.tsx';
import { AlarmsScreen } from './screens/AlarmsScreen.tsx';
import { CompaniesScreen } from './screens/CompaniesScreen.tsx';
import { SitesScreen } from './screens/SitesScreen.tsx';
import { SiteDetailScreen } from './screens/SiteDetailScreen.tsx';
import { ImportsScreen } from './screens/ImportsScreen.tsx';
import { ImportDetailScreen } from './screens/ImportDetailScreen.tsx';
import { IngestScreen } from './screens/IngestScreen.tsx';
import { ErpSyncScreen } from './screens/ErpSyncScreen.tsx';
import { ConsoleScreen } from './console/ConsoleScreen.tsx';
import { MatrixScreen } from './console/Matrix.tsx';
import { VideoPopout } from './console/VideoPopout.tsx';
import { EventsScreen } from './screens/EventsScreen.tsx';
import { SetupScreen } from './screens/setup/SetupScreen.tsx';
import { ReportsScreen } from './screens/ReportsScreen.tsx';
import { AuditScreen } from './screens/AuditScreen.tsx';
import { WallboardScreen } from './screens/WallboardScreen.tsx';
// Phase 3 supervisor screens load on first use, like MSAL, so they never block first paint.
const QaScreen = lazy(() => import('./screens/QaScreen.tsx').then((m) => ({ default: m.QaScreen })));
const AiReportScreen = lazy(() => import('./screens/AiReportScreen.tsx').then((m) => ({ default: m.AiReportScreen })));
import { ToastProvider } from './console/Toasts.tsx';
import { CopilotProvider } from './live/copilot.tsx';
import { LiveLayer } from './live/LiveLayer.tsx';
import { PresenceProvider } from './live/presence.tsx';

function Forbidden({ what, roles = 'admin' }: { what: string; roles?: string }) {
  return (
    <div className="page">
      <div className="errbox" role="alert">
        <Icon name="lock" />
        <div>
          <b>Not available for your role</b>
          <div>
            {what} needs the {roles} role. Ask an admin, or switch identity in dev mode.
          </div>
        </div>
      </div>
    </div>
  );
}

function Screen({ route }: { route: Route }) {
  const s = useSession();
  switch (route.name) {
    case 'console':
      return s.has('operator', 'supervisor', 'admin') ? <ConsoleScreen eventId={route.eventId} /> : <Forbidden what="The operator console" roles="operator, supervisor or admin" />;
    case 'matrix':
    case 'video':
      // Rendered by Shell without the app chrome.
      return null;
    case 'events':
      return s.has('supervisor', 'admin') ? <EventsScreen state={route.state} /> : <Forbidden what="Supervisor events" roles="supervisor or admin" />;
    case 'setup':
      return s.has('supervisor', 'admin') ? <SetupScreen section={route.section} id={route.id} tab={route.tab} /> : <Forbidden what="Setup" roles="admin (or supervisor to read)" />;
    case 'wallboard':
      return s.has('supervisor', 'admin') ? <WallboardScreen /> : <Forbidden what="The supervisor wallboard" roles="supervisor or admin" />;
    case 'qa':
      return s.has('supervisor', 'admin') ? <Suspense fallback={<Loading label="Loading…" />}><QaScreen tab={route.tab} eventId={route.eventId} /></Suspense> : <Forbidden what="AI QA review" roles="supervisor or admin" />;
    case 'aiReport':
      return s.has('supervisor', 'admin') ? <Suspense fallback={<Loading label="Loading…" />}><AiReportScreen days={route.days} /></Suspense> : <Forbidden what="The AI report" roles="supervisor or admin" />;
    case 'reports':
      return s.has('supervisor', 'admin') ? <ReportsScreen /> : <Forbidden what="Exports" roles="supervisor or admin" />;
    case 'audit':
      return s.has('supervisor', 'admin', 'auditor') ? <AuditScreen /> : <Forbidden what="The audit log" roles="supervisor, admin or auditor" />;
    case 'alarms':
      return <AlarmsScreen key={`${route.siteId ?? ''}|${route.flag ?? ''}`} siteId={route.siteId} flag={route.flag} />;
    case 'companies':
      return <CompaniesScreen />;
    case 'sites':
      return <SitesScreen companyId={route.companyId} />;
    case 'site':
      return <SiteDetailScreen id={route.id} tab={route.tab} />;
    case 'imports':
      return <ImportsScreen />;
    case 'import':
      return <ImportDetailScreen id={route.id} />;
    case 'ingest':
      return s.isAdmin ? <IngestScreen /> : <Forbidden what="Alarm ingest" />;
    case 'erpSync':
      return s.has('supervisor', 'admin') ? <ErpSyncScreen tab={route.tab} /> : <Forbidden what="ERP Sync" roles="supervisor or admin" />;
    case 'notFound':
      return (
        <div className="page">
          <div className="state empty">
            No screen at <span className="mono">{route.path || '/'}</span>.{' '}
            <a href="#/alarms">Go to the alarm list</a>
          </div>
        </div>
      );
  }
}

/** Phase 4: approvals, agent replies and watch hits on every screen (features.agent, people who handle events). */
function MaybeCopilot({ on, children }: { on: boolean; children: ReactNode }) {
  return on ? <CopilotProvider>{children}</CopilotProvider> : <>{children}</>;
}

function Shell() {
  const route = useRoute();
  const s = useSession();
  const home = s.has('operator') ? '/console' : '/alarms';
  const canRoute = s.has('operator', 'supervisor', 'admin');
  const softphone = canRoute ? softphoneFor(s.config) : null;
  const [phoneSlot, setPhoneSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!window.location.hash || window.location.hash === '#' || window.location.hash === '#/') navigate(home, { replace: true });
  }, [home]);
  useEffect(() => {
    const titles: Record<Route['name'], string> = {
      console: 'Console',
      matrix: 'Camera matrix',
      video: 'Video',
      events: 'Events',
      setup: 'Setup',
      wallboard: 'Wallboard',
      qa: 'QA review',
      aiReport: 'AI report',
      reports: 'Reports',
      audit: 'Audit',
      alarms: 'Alarms',
      companies: 'Companies',
      sites: 'Sites',
      site: 'Site',
      imports: 'Imports',
      import: 'Import',
      ingest: 'Alarm ingest',
      erpSync: 'ERP Sync',
      notFound: 'Not found',
    };
    document.title = `${titles[route.name]} · EyeQ SOC`;
  }, [route.name]);
  // The pop-out camera matrix (its own browser window): no header, presence, offers, softphone or co-pilot layer.
  if (route.name === 'matrix') return <MatrixScreen key={route.eventId ?? ''} eventId={route.eventId} />;
  // Phase 5: the pop-out main video (same channel as the matrix).
  if (route.name === 'video') return <VideoPopout key={route.eventId ?? ''} eventId={route.eventId} />;
  return (
    <PresenceProvider>
    <ToastProvider>
    <MaybeCopilot on={canRoute && s.config.features.agent}>
    <div id="app" className={route.name === 'console' ? 'fixed' : undefined}>
      <a className="skip" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById('main')?.focus(); }}>
        Skip to content
      </a>
      <Header route={route} phoneSlot={softphone ? setPhoneSlot : undefined} />
      {/* The phone's pill goes in the header; its panel drops down from there (and comes right after it for Tab). */}
      {softphone && <CallPanel sp={softphone} slot={phoneSlot} />}
      <main id="main" tabIndex={-1}>
        <Screen route={route} />
      </main>
      {canRoute && <LiveLayer />}
    </div>
    </MaybeCopilot>
    </ToastProvider>
    </PresenceProvider>
  );
}

/** The EyeQ Monitoring logo above the start-up, sign-in and error screens. */
function BootLogo() {
  return <img className="boot-logo" src="/brand/eyeq-logo-on-dark.png" alt="EyeQ Monitoring" />;
}

type Boot =
  | { phase: 'starting' }
  | { phase: 'failed'; error: unknown }
  | { phase: 'ready'; config: AppConfig };

export function App() {
  const [boot, setBoot] = useState<Boot>({ phase: 'starting' });
  const [bootTry, setBootTry] = useState(0);
  const [epoch, setEpoch] = useState(0);
  const [me, setMe] = useState<{ me: Me | null; error: unknown; epoch: number } | null>(null);

  useEffect(() => {
    let live = true;
    setBoot({ phase: 'starting' });
    (async () => {
      const config = await loadConfig();
      const auth = await createAuth(config);
      setAuthProvider(auth);
      if (live) setBoot({ phase: 'ready', config });
    })().catch((error: unknown) => live && setBoot({ phase: 'failed', error }));
    return () => {
      live = false;
    };
  }, [bootTry]);

  useEffect(() => {
    if (boot.phase !== 'ready') return;
    const ctl = new AbortController();
    setMe(null);
    api.me(ctl.signal).then(
      (m) => setMe({ me: m, error: null, epoch }),
      (error: unknown) => {
        if (!ctl.signal.aborted) setMe({ me: null, error, epoch });
      },
    );
    return () => ctl.abort();
  }, [boot.phase, epoch]);

  const reload = useCallback(() => setEpoch((e) => e + 1), []);
  const config = boot.phase === 'ready' ? boot.config : null;
  const session = useMemo(() => (config && me?.me ? makeSession(config, me.me, reload) : null), [config, me, reload]);

  if (boot.phase === 'starting') {
    return (
      <div className="boot">
        <BootLogo />
        <Loading label="Starting EyeQ SOC…" />
      </div>
    );
  }
  if (boot.phase === 'failed') {
    return (
      <div className="boot">
        <BootLogo />
        <ErrorBox title="The console could not start" error={boot.error} onRetry={() => setBootTry((n) => n + 1)} />
      </div>
    );
  }
  if (!me) {
    return (
      <div className="boot">
        <BootLogo />
        <Loading label="Signing in…" />
      </div>
    );
  }
  if (!session) {
    return (
      <div className="boot">
        <BootLogo />
        <ErrorBox title="Could not load your user (/api/me)" error={me.error} onRetry={reload} />
        {boot.config.authMode === 'dev' && (
          <div className="boot-dev">
            <span className="note">Dev mode: check the identity sent to the API.</span>
            <DevSwitcher onChange={reload} startOpen />
          </div>
        )}
      </div>
    );
  }
  return (
    <SessionContext.Provider value={session}>
      <StreamProvider key={epoch}>
        <Shell key={epoch} />
      </StreamProvider>
    </SessionContext.Provider>
  );
}
